<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverClose } from "reka-ui";
import { useRouter } from "vue-router";

import { useMessageStore, useRetentionStore } from "@/stores";

import { isApiError } from "@/services/Api";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import RetentionActionPopover from "./RetentionActionPopover.vue";
import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

withDefaults(defineProps<{
  requesterName: string | null;
  deletionReason: string | null;
}>(), {  })

const router = useRouter();
const caseRouteParams = useCaseRouteParams();

const caseRefInput = ref("");

const state = ref<{ status: "idle" } | { status: "loading" } | { status: "error"; message: string }>({
  status: "idle",
});

const isSubmitDisabled = computed(
  () => state.value.status === "loading" || caseRefInput.value !== caseRouteParams.value.casename,
);

async function handleSubmit() {
  if (isSubmitDisabled.value) return;

  state.value = { status: "loading" };

  try {
    await useRetentionStore().approveDeletion(undefined);

    state.value = { status: "idle" };

    router.push({ name: "brand", params: { brand: caseRouteParams.value.brand } });

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Deletion request approved.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to approve deletion. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}
</script>
<template>
  <RetentionActionPopover>
    <template #trigger>
      <button type="button" class="tw-btn-md-gray tw-b-r">Approve Deletion</button>
    </template>
    <form class="tw-flex tw-flex-col tw-gap-2" @submit.prevent="handleSubmit">
      <p class="tw-text-sm tw-font-semibold">Deletion request details</p>
      <dl class="tw-grid tw-grid-cols-[max-content_1fr] tw-gap-x-3 tw-gap-y-1 tw-text-sm">
        <template v-if="requesterName != null">
          <dt>Requested by</dt>
          <dd>{{ requesterName }}</dd>
        </template>
        <dt>Reason</dt>
        <dd>{{ deletionReason ?? "No reason provided" }}</dd>
      </dl>
      <hr />
      <p class="tw-max-w-md tw-text-balance tw-text-sm">
        I approve this deletion and release this case for processing.
      </p>
      <RetentionActionConfirmCase v-model:case-ref="caseRefInput" :require-reason="false" />
      <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ state.message }}
      </p>
      <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
        <PopoverClose class="tw-btn-md-white tw-b-r">Cancel</PopoverClose>
        <button
          type="submit"
          class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1"
          :disabled="isSubmitDisabled"
        >
          <i v-if="state.status === 'loading'" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
          Approve Deletion
        </button>
      </div>
    </form>
  </RetentionActionPopover>
</template>
